import React, { useEffect, useMemo, useState, useRef } from "react";
import fbxLoadingGif from "../../../../assets/Logo/FBX-Gif.gif";

const DigitalScouting = ({
    reportData,
    reportId,
    reportPosition = "-",
    canEdit = true,
    onDigitalChange = null, // (payload) => void
    onAiSummary = null, // ({ positive, negative, summary }, mode) => Promise<void>
}) => {
    const percentLabels = [
        { value: 0, label: "-", color: "bg-white text-dark" },
        { value: 10, label: "10%", color: "bg-danger text-white" },
        { value: 20, label: "20%", color: "bg-warning text-white" },
        { value: 30, label: "30%", color: "bg-brown text-white" },
        { value: 40, label: "40%", color: "bg-lemon text-dark" },
        { value: 50, label: "50%", color: "bg-lemon text-dark" },
        { value: 60, label: "60%", color: "bg-success text-white" },
        { value: 70, label: "70%", color: "bg-success text-white" },
        { value: 80, label: "80%", color: "bg-info text-white" },
        { value: 90, label: "90%", color: "bg-info text-white" },
        { value: 100, label: "100%", color: "btn-purple text-white" },
    ];

    const userEditedRef = useRef(false);

    // Unique options (0,10,...,100) for the inline picker
    const miniOptions = useMemo(() => {
        const unique = [];
        percentLabels.forEach((p) => {
            if (!unique.some((u) => u.value === p.value)) {
                unique.push(p);
            }
        });
        return unique;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Overall average of header values (PS#)
    const [overallAverage, setOverallAverage] = useState(null);

    // Convert ALL-CAPS strings to sentence case ("INITIAL QUICK" -> "Initial quick").
    const toSentenceCase = (text) => {
        if (!text || typeof text !== "string") return text;
        const trimmed = text.trim();
        if (trimmed === "" || trimmed === "0" || trimmed === "-") return "-";

        // If there's already a lowercase letter, leave it as-is
        if (/[a-z]/.test(trimmed)) {
            return trimmed;
        }

        const lower = trimmed.toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    };

    // Parse a raw value like "70", "70%", "72.5", " 80 % " into a number (0–100, float allowed)
    const parsePercentValue = (raw) => {
        if (raw === undefined || raw === null) return null;
        const str = String(raw).trim();
        if (!str) return null;

        const match = str.match(/(\d+(\.\d+)?)/); // capture int or float
        if (!match) return null;

        let num = parseFloat(match[1]);
        if (Number.isNaN(num)) return null;

        if (num < 0) num = 0;
        if (num > 100) num = 100;
        return num;
    };

    // Given a raw value, find the matching color class from percentLabels
    const getColorForValue = (raw) => {
        const num = parsePercentValue(raw);
        if (num === null) {
            const zero = percentLabels.find((p) => p.value === 0);
            return zero ? zero.color : "btn-outline-secondary";
        }

        // Round to nearest 10 (0,10,20...) — .5 rounds up
        const rounded = Math.round(num / 10) * 10;

        const bucket = percentLabels.find((p) => p.value === rounded);
        if (bucket) return bucket.color;

        const zero = percentLabels.find((p) => p.value === 0);
        return zero ? zero.color : "btn-outline-secondary";
    };

    // Compute average of values > 0 in a subVals array
    const computeHeaderAverage = (subVals) => {
        const nums = (subVals || [])
            .map((v) => parsePercentValue(v))
            .filter((n) => n !== null && n > 0);

        if (!nums.length) return "";

        const sum = nums.reduce((acc, n) => acc + n, 0);
        const avg = sum / nums.length;
        // Keep one decimal for display
        return avg.toFixed(1);
    };

    // Local state: blocks derived directly from reportData (which is already blocks)
    const [blocks, setBlocks] = useState([]);

    // Which picker is open: { blockIndex, subIndex, left, top, width } or null
    const [activePicker, setActivePicker] = useState(null);
    const [isAskingAi, setIsAskingAi] = useState(false);
    const [aiAnswer, setAiAnswer] = useState("");
    const [aiError, setAiError] = useState("");
    const [aiPayloadPreview, setAiPayloadPreview] = useState(null);
    const [showAiPopup, setShowAiPopup] = useState(false);
    const [aiCopyStatus, setAiCopyStatus] = useState("");
    const [aiSummaryStatus, setAiSummaryStatus] = useState("");
    const [isSendingToSummary, setIsSendingToSummary] = useState(false);
    const [aiReportSections, setAiReportSections] = useState(null);

    // --- IMPORTANT ---
    // Hydrate from reportData ONLY when switching to a different reportId (or first time),
    // so we don't create a parent<->child loop if parent stores updated digital blocks.
    const hydratedKeyRef = useRef(null);

    useEffect(() => {
        const key = String(reportId ?? "NO_REPORT_ID");

        const shouldHydrate =
            hydratedKeyRef.current !== key || (blocks?.length ?? 0) === 0;

        if (!shouldHydrate) return;

        hydratedKeyRef.current = key;
        userEditedRef.current = false;

        if (Array.isArray(reportData)) {
            const normalized = reportData.map((block) => {
                const subDescs = Array.isArray(block.subDescs)
                    ? block.subDescs
                    : [];
                const subVals = Array.isArray(block.subVals) ? block.subVals : [];

                // Ensure subVals length matches subDescs length
                const newSubVals = [...subVals];
                while (newSubVals.length < subDescs.length) newSubVals.push("");

                // Always recompute headerValue from the subs on load
                const newHeaderValue = computeHeaderAverage(newSubVals);

                return {
                    ...block,
                    subDescs,
                    subVals: newSubVals,
                    headerValue: newHeaderValue,
                };
            });

            setBlocks(normalized);
        } else {
            setBlocks([]);
        }

        setActivePicker(null); // reset pickers when report changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportId, reportData]);

    // Handle click on a sub-value cell (open/close picker)
    const handleSubClick = (blockIndex, subIndex, element) => {
        if (!canEdit) return;

        setActivePicker((current) => {
            // Toggle off if same one clicked again
            if (
                current &&
                current.blockIndex === blockIndex &&
                current.subIndex === subIndex
            ) {
                return null;
            }

            // If no element (very defensive), just open near the top-left.
            if (!element) {
                return { blockIndex, subIndex, left: 8, top: 8, width: 360 };
            }

            const rect = element.getBoundingClientRect();
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            const viewportPadding = 8;
            const preferredWidth = 360;
            const popupWidth = Math.min(preferredWidth, viewportWidth - (viewportPadding * 2));
            const popupHeight = 76;
            const spaceRight = viewportWidth - rect.right - viewportPadding;
            const spaceLeft = rect.left;
            const preferredLeft = spaceRight >= popupWidth
                ? rect.right + 4
                : spaceLeft >= popupWidth
                    ? rect.left - popupWidth - 4
                    : rect.left + (rect.width / 2) - (popupWidth / 2);

            const left = Math.max(
                viewportPadding,
                Math.min(preferredLeft, viewportWidth - popupWidth - viewportPadding)
            );
            const top = Math.max(
                viewportPadding,
                Math.min(rect.top, viewportHeight - popupHeight - viewportPadding)
            );

            return { blockIndex, subIndex, left, top, width: popupWidth };
        });
    };

    // Handle selecting a value from the mini skill bar
    const handleValueSelect = (blockIndex, subIndex, value) => {
        if (!canEdit) return;

        userEditedRef.current = true; // <-- add this

        setBlocks((prev) =>
            prev.map((block, i) => {
                if (i !== blockIndex) return block;

                const newSubVals = Array.isArray(block.subVals)
                    ? [...block.subVals]
                    : [];

                // Ensure length ≥ subIndex + 1
                while (newSubVals.length <= subIndex) {
                    newSubVals.push("");
                }

                newSubVals[subIndex] = value;

                // Recompute header for this block
                const newHeaderValue = computeHeaderAverage(newSubVals);

                return {
                    ...block,
                    subVals: newSubVals,
                    headerValue: newHeaderValue,
                };
            })
        );

        // Close picker after selection
        setActivePicker(null);
    };

    // Compute overallAverage from blocks. DO NOT setBlocks here.
    useEffect(() => {
        const headerNums = blocks
            .map((block) => parsePercentValue(block.headerValue))
            .filter((n) => n !== null && n > 0);

        if (headerNums.length > 0) {
            const sum = headerNums.reduce((acc, n) => acc + n, 0);
            const average = sum / headerNums.length;
            setOverallAverage(average);
        } else {
            setOverallAverage(null);
        }
    }, [blocks]);

    // Push updates upward (on mount + on every change), but avoid spam duplicates
    const lastSentRef = useRef("");

    useEffect(() => {
        if (typeof onDigitalChange !== "function") return;

        // Don't push anything upward until the user actually changes something
        if (!userEditedRef.current) return;

        const digitalBlocks = blocks.map((block) => ({
            psIndex: block.psIndex,
            headerDesc: block.headerDesc,
            headerValue: block.headerValue,
            subDescs: block.subDescs,
            subVals: block.subVals,
        }));

        const payload = {
            reportId: reportId ?? null,
            overallAverage,
            digitalBlocks,
        };

        const sig = JSON.stringify(payload);
        if (sig === lastSentRef.current) return;
        lastSentRef.current = sig;

        onDigitalChange(payload);
    }, [blocks, overallAverage, reportId, onDigitalChange]);

    const hasScoutingValue = (raw) => {
        if (raw === undefined || raw === null) return false;
        const value = String(raw).trim();
        return value !== "" && value !== "-" && value !== "0";
    };

    const buildAiPayload = () => {
        const digitalFields = blocks
            .map((block) => {
                const subDescs = Array.isArray(block.subDescs) ? block.subDescs : [];
                const subVals = Array.isArray(block.subVals) ? block.subVals : [];

                return {
                    psIndex: block.psIndex,
                    category: toSentenceCase(block.headerDesc),
                    categoryAverage: block.headerValue || "",
                    fields: subDescs
                        .map((desc, index) => ({
                            label: toSentenceCase(desc),
                            value: subVals[index] ?? "",
                        }))
                        .filter((field) => {
                            const label = String(field.label ?? "").trim();
                            return label && label !== "-" && label !== "0" && hasScoutingValue(field.value);
                        }),
                };
            })
            .filter((block) => block.fields.length > 0);

        const promptLines = digitalFields.flatMap((block) => [
            `${block.category || "Category"}: ${block.categoryAverage || "-"}`,
            ...block.fields.map((field) => `- ${field.label}: ${field.value || "-"}`),
        ]);

        const prompt = [
            "Can you tell me about this player based on the report position and the digital scouting fields?",
            `Report position: ${reportPosition || "-"}`,
            overallAverage !== null ? `Overall digital average: ${overallAverage.toFixed(1)}%` : "",
            "Use the numeric field values internally, but do not include any grades, percentages, scores, or parenthetical grade values such as (80) in the write-up.",
            "Return exactly valid JSON with these string keys only: positive, negative, summary.",
            "Do not include markdown, code fences, bullets, or any text outside the JSON object.",
            "Digital scouting fields:",
            ...promptLines,
        ]
            .filter(Boolean)
            .join("\n");

        return {
            question: "Can you tell me about this player?",
            message: prompt,
            prompt,
            reportId: reportId ?? null,
            reportPosition: reportPosition || "-",
            overallAverage: overallAverage !== null ? Number(overallAverage.toFixed(1)) : null,
            digitalFields,
        };
    };

    const cleanSectionText = (text) =>
        String(text || "")
            .replace(/\s*\(\s*\d+(\.\d+)?\s*%?\s*\)/g, "")
            .replace(/\b\d+(\.\d+)?\s*%\b/g, "")
            .replace(/\s{2,}/g, " ")
            .trim();

    const parseAiReportSections = (answer) => {
        const raw = String(answer || "").trim();
        if (!raw) return { positive: "", negative: "", summary: "" };

        const jsonText = raw
            .replace(/^```(?:json)?/i, "")
            .replace(/```$/i, "")
            .trim();

        try {
            const parsed = JSON.parse(jsonText);
            return {
                positive: cleanSectionText(parsed.positive),
                negative: cleanSectionText(parsed.negative),
                summary: cleanSectionText(parsed.summary),
            };
        } catch (err) {
            const grab = (label, nextLabels) => {
                const next = nextLabels.join("|");
                const regex = new RegExp(`${label}\\s*:?\\s*([\\s\\S]*?)(?=\\n\\s*(?:${next})\\s*:?|$)`, "i");
                const match = raw.match(regex);
                return cleanSectionText(match?.[1] || "");
            };

            return {
                positive: grab("positive", ["negative", "summary"]),
                negative: grab("negative", ["positive", "summary"]),
                summary: grab("summary", ["positive", "negative"]),
            };
        }
    };

    const formatAiReportSections = (sections) => {
        const positive = cleanSectionText(sections?.positive);
        const negative = cleanSectionText(sections?.negative);
        const summary = cleanSectionText(sections?.summary);

        return [
            positive ? `Positive\n${positive}` : "",
            negative ? `Negative\n${negative}` : "",
            summary ? `Summary\n${summary}` : "",
        ]
            .filter(Boolean)
            .join("\n\n");
    };

    const readAiAnswer = (data) => {
        if (typeof data === "string") return data;
        if (!data || typeof data !== "object") return "";

        return (
            data.error ||
            data.answer ||
            data.response ||
            data.message ||
            data.text ||
            data.content ||
            data.result ||
            ""
        );
    };

    const handleAskAi = async () => {
        if (blocks.length === 0 || isAskingAi) return;

        setIsAskingAi(true);
        setAiAnswer("");
        setAiError("");
        setAiCopyStatus("");
        setAiSummaryStatus("");
        setAiReportSections(null);

        try {
            const endpoint =
                process.env.REACT_APP_SCOUT_AI_ENDPOINT ||
                "https://www.fbxchange.com/ReactPHP/ask-ai.php";
            const payload = buildAiPayload();

            setAiPayloadPreview(payload);
            setShowAiPopup(true);

            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            const text = await res.text();
            let data = text;

            try {
                data = text ? JSON.parse(text) : "";
            } catch (err) {
                data = text;
            }

            if (!res.ok) {
                const msg = readAiAnswer(data) || `AI request failed (${res.status}).`;
                throw new Error(msg);
            }

            if (data && typeof data === "object" && data.ok === false) {
                throw new Error(data.error || data.message || "AI request failed.");
            }

            const answer = readAiAnswer(data) || JSON.stringify(data, null, 2);
            const sections = parseAiReportSections(answer);
            setAiReportSections(sections);
            setAiAnswer(formatAiReportSections(sections) || answer);
        } catch (err) {
            console.error("Ask AI failed:", err);
            setAiError(err?.message || "AI request failed.");
        } finally {
            setIsAskingAi(false);
        }
    };

    const handleCopyAiAnswer = async () => {
        if (!aiAnswer) return;

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(aiAnswer);
            } else {
                const textarea = document.createElement("textarea");
                textarea.value = aiAnswer;
                textarea.setAttribute("readonly", "");
                textarea.style.position = "fixed";
                textarea.style.left = "-9999px";
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);
            }

            setAiCopyStatus("Copied");
            window.setTimeout(() => setAiCopyStatus(""), 1600);
        } catch (err) {
            console.error("Copy AI answer failed:", err);
            setAiCopyStatus("Copy failed");
        }
    };

    const handleAiSummary = async (mode) => {
        if (!aiReportSections || typeof onAiSummary !== "function" || isSendingToSummary) return;

        setIsSendingToSummary(true);
        setAiSummaryStatus("");

        try {
            await onAiSummary(aiReportSections, mode);
            setAiSummaryStatus(mode === "replace" ? "Processed" : "Merged");
            window.setTimeout(() => setAiSummaryStatus(""), 2000);
        } catch (err) {
            console.error("AI summary update failed:", err);
            setAiSummaryStatus("Report update failed");
        } finally {
            setIsSendingToSummary(false);
        }
    };

    return (
        <div id="DIGITAL_BOX" className="row g-2 mb-2">
            {/* Percent skill bar */}
            <div
                id="skillBar"
                className="bg-dark text-white-50 rounded px-2 py-2"
                style={{ opacity: 1 }}
            >
                <div
                    className="row g-2 align-items-center"
                    style={{ height: "45px" }}
                >
                    {/* Left: 0–100 scale buttons */}
                    <div className="col-36 d-flex align-items-center">
                        <div className="d-flex flex-wrap justify-content-start w-100">
                            {percentLabels.map(({ label, color }, i) => (
                                <div
                                    key={i}
                                    style={{
                                        width: "calc(8%)",
                                        marginRight: "4px",
                                        marginBottom: "0px",
                                    }}
                                >
                                    <div
                                        className={`rounded text-center ${color} fs-7 py-0 px-2`}
                                        style={{
                                            lineHeight: "22px",
                                            minWidth: "30px",
                                            textAlign: "center",
                                        }}
                                    >
                                        {label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: big overall average for this report */}
                    <div className="col-12 d-flex justify-content-end align-items-center gap-2">
                        <div
                            className={`choice_button rounded fw-bold ${getColorForValue(
                                overallAverage
                            )}`}
                            style={{
                                lineHeight: "30px",
                                height: "30px",
                                padding: "0 18px",
                                display: "inline-block",
                                textAlign: "center",
                                minWidth: "110px",
                                fontSize: "0.9rem",
                                borderColor: "rgba(255,255,255,0.6)",
                            }}
                        >
                            {overallAverage !== null
                                ? `${overallAverage.toFixed(1)}%`
                                : "-"}
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-light fw-bold"
                            onClick={handleAskAi}
                            disabled={isAskingAi || blocks.length === 0}
                            title="Ask AI about this digital scout report"
                            style={{ whiteSpace: "nowrap", minWidth: "84px" }}
                        >
                            {isAskingAi ? "Working..." : "Ask AI"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Digital output metric boxes */}
            <div className="row g-2" id="digital_output">
                {blocks.length === 0 ? (
                    <div className="col-12 text-muted small ps-2">
                        <em>No digital scouting data.</em>
                    </div>
                ) : (
                    blocks.map((block, blockIndex) => (
                        <div
                            key={block.psIndex ?? blockIndex}
                            className="border rounded fs-7"
                            style={{
                                width: "20%",
                                position: "relative",
                                overflow: "visible",
                            }}
                        >
                            {/* Header row: PS#D description + PS# value (average) */}
                            {block.headerDesc !== "-" && block.headerDesc !== "0" && (
                                <div className="row g-0">
                                    <div
                                        className="ps-2 col-39 bg-black text-white text-truncate"
                                        style={{ lineHeight: "35px" }}
                                    >
                                        {toSentenceCase(block.headerDesc) || "\u00A0"}
                                    </div>
                                    <div className="col-9 text-center">
                                        <div
                                            className={`number_button_white text-center border border-1 border-navy ${getColorForValue(
                                                block.headerValue
                                            )}`}
                                            style={{ lineHeight: "35px" }}
                                        >
                                            {block.headerValue || "\u00A0"}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Sub rows: description + editable value */}
                            {(block.subDescs || []).map((subText, subIndex) => {
                                const descHasText =
                                    subText &&
                                    String(subText).trim() !== "" &&
                                    String(subText).trim() !== "-";

                                const rawSubVal = (block.subVals || [])[subIndex];

                                const isZero =
                                    rawSubVal === 0 ||
                                    rawSubVal === "0" ||
                                    (rawSubVal != null && String(rawSubVal).trim() === "0");

                                const displaySubVal = isZero
                                    ? "-"
                                    : rawSubVal === undefined ||
                                      rawSubVal === null ||
                                      String(rawSubVal).trim() === ""
                                    ? "\u00A0"
                                    : String(rawSubVal).trim();

                                return (
                                    <React.Fragment key={subIndex}>
                                        <div className="row g-0">
                                            {/* LEFT: description */}
                                            <div
                                                className="col-39 ps-2 bg-light text-truncate"
                                                style={{ lineHeight: "35px" }}
                                            >
                                                {toSentenceCase(subText) || "\u00A0"}
                                            </div>

                                            {/* RIGHT: numeric/value */}
                                            <div className="col-9 text-center position-relative">
                                                <div
                                                    className={`number_button_white text-center ${getColorForValue(
                                                        rawSubVal
                                                    )}`}
                                                    style={{
                                                        lineHeight: "35px",
                                                        cursor:
                                                            canEdit && descHasText
                                                                ? "pointer"
                                                                : "default",
                                                        position: "relative",
                                                        zIndex: 5,
                                                        opacity:
                                                            !canEdit && descHasText
                                                                ? 0.75
                                                                : 1,
                                                    }}
                                                    onClick={
                                                        canEdit && descHasText
                                                            ? (e) =>
                                                                  handleSubClick(
                                                                      blockIndex,
                                                                      subIndex,
                                                                      e.currentTarget
                                                                  )
                                                            : undefined
                                                    }
                                                >
                                                    {displaySubVal}
                                                </div>

                                                {/* Fly-out mini bar */}
                                                {canEdit &&
                                                    descHasText &&
                                                    activePicker &&
                                                    activePicker.blockIndex === blockIndex &&
                                                    activePicker.subIndex === subIndex && (
                                                        <div
                                                            className="bg-dark text-white rounded-1 shadow"
                                                            style={{
                                                                position: "fixed",
                                                                top: `${activePicker.top}px`,
                                                                left: `${activePicker.left}px`,
                                                                width: `${activePicker.width}px`,
                                                                zIndex: 1050,
                                                                display: "grid",
                                                                gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
                                                                padding: "4px 6px",
                                                                gap: "4px",
                                                            }}
                                                        >
                                                            {miniOptions.map((opt) => (
                                                                <button
                                                                    key={opt.value}
                                                                    type="button"
                                                                    className={`btn btn-sm flex-grow-1 ${getColorForValue(
                                                                        opt.value
                                                                    )}`}
                                                                    onClick={() =>
                                                                        handleValueSelect(
                                                                            blockIndex,
                                                                            subIndex,
                                                                            opt.value
                                                                        )
                                                                    }
                                                                >
                                                                    {opt.value}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    ))
                )}
            </div>

            {showAiPopup && aiPayloadPreview && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ background: "rgba(0,0,0,0.45)", zIndex: 12000 }}
                >
                    <div
                        className="bg-white text-dark border rounded shadow p-3"
                        style={{ width: "min(980px, calc(100vw - 32px))", maxHeight: "80vh", overflowY: "auto" }}
                    >
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                            <div className="fw-bold">
                                {isAskingAi ? "AI is working..." : aiError ? "AI request failed" : "AI request sent"}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                {!isAskingAi && aiAnswer && (
                                    <>
                                        {(aiCopyStatus || aiSummaryStatus) && (
                                            <span className="small text-muted">
                                                {aiSummaryStatus || aiCopyStatus}
                                            </span>
                                        )}
                                        {typeof onAiSummary === "function" && aiReportSections && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-success"
                                                    onClick={() => handleAiSummary("merge")}
                                                    disabled={isSendingToSummary}
                                                >
                                                    Merge with Current Content
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-success"
                                                    onClick={() => handleAiSummary("replace")}
                                                    disabled={isSendingToSummary}
                                                >
                                                    Overwrite Current Content
                                                </button>
                                            </>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={handleCopyAiAnswer}
                                        >
                                            Copy
                                        </button>
                                    </>
                                )}
                                {!isAskingAi && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setShowAiPopup(false)}
                                    >
                                        Close
                                    </button>
                                )}
                            </div>
                        </div>
                        {isAskingAi && (
                            <div className="border rounded p-3 small mb-3 bg-light text-dark text-center">
                                <img
                                    src={fbxLoadingGif}
                                    alt="AI is working"
                                    className="mb-2"
                                    style={{ width: 86, height: 86, objectFit: "contain" }}
                                />
                                <div className="fw-bold fs-6">AI Working</div>
                                <div className="text-muted">This can take up to 1 minute. Be Patient.</div>
                            </div>
                        )}
                        {(aiAnswer || aiError) && (
                            <div className={`border rounded p-2 small mb-3 ${aiError ? "bg-warning-subtle text-dark" : "bg-light text-dark"}`}>
                                <div className="fw-bold mb-1">
                                    {aiError ? "AI Error" : "Here is what AI said"}
                                </div>
                                <div style={{ whiteSpace: "pre-wrap" }}>{aiError || aiAnswer}</div>
                            </div>
                        )}
                        <div className="small mb-2">
                            <span className="text-muted">Position:</span>{" "}
                            <span className="fw-bold">{aiPayloadPreview.reportPosition || "-"}</span>
                        </div>
                        <div className="small fw-bold mb-1">Fields sent</div>
                        {aiPayloadPreview.digitalFields.length === 0 ? (
                            <div className="small text-muted">No filled digital fields.</div>
                        ) : (
                            <div
                                className="small"
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                                    columnGap: "14px",
                                    rowGap: "8px",
                                }}
                            >
                                {aiPayloadPreview.digitalFields.map((block) => (
                                    <div
                                        key={block.psIndex ?? block.category}
                                        className="border rounded bg-light px-2 py-1"
                                        style={{ minWidth: 0 }}
                                    >
                                        <div className="fw-bold">
                                            {block.category || "Category"}
                                            {block.categoryAverage ? ` (${block.categoryAverage})` : ""}
                                        </div>
                                        {block.fields.map((field) => (
                                            <div
                                                key={`${block.psIndex}-${field.label}`}
                                                className="ps-2 text-truncate"
                                                title={`${field.label}: ${field.value}`}
                                            >
                                                {field.label}: <span className="fw-bold">{field.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DigitalScouting;
